<template>
  <div class="qr-login-form">
    <!-- 2FA Verification State -->
    <TwoFactorVerification
      v-if="requires2FA"
      :masked-email="maskedEmail"
      :is-verifying="isVerifying2FA"
      ref="tfaComponent"
      @verify="handle2FAVerify"
      @resend="handle2FAResend"
      @cancel="cancel2FA"
    />

    <!-- Loading state -->
    <div v-else-if="isLoading && !qrSession" class="loading-state">
      <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="4" />
      <p class="mt-3">Generating QR code...</p>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="status-state">
      <div class="status-placeholder status-placeholder--error">
        <div class="status-badge status-badge--error">
          <i class="pi pi-exclamation-triangle"></i>
        </div>
        <p class="status-title">Something went wrong</p>
        <p class="status-message">{{ error }}</p>
      </div>
      <Button label="Try again" icon="pi pi-refresh" @click="createNewSession" />
    </div>

    <!-- Expired state -->
    <div v-else-if="isExpired" class="status-state">
      <div class="status-placeholder">
        <p class="status-title">QR code expired</p>
        <p class="status-message">Request a new code to continue signing in.</p>
      </div>
      <Button label="Refresh QR code" icon="pi pi-refresh" @click="createNewSession" />
    </div>

    <!-- QR Code display -->
    <div v-else-if="qrSession" class="qr-display">
      <div class="qr-code-container">
        <canvas ref="qrCanvas"></canvas>
      </div>
      <ol class="scan-steps">
        <li>Open the <strong>Koll</strong> app</li>
        <li>Tap <i class="pi pi-cog scan-steps__icon"></i> in the top right corner</li>
        <li>Select <strong>Web Login</strong> and scan</li>
      </ol>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { useAuthStore } from '@/stores/authStore'
import qrLoginService from '@/services/qrLoginService'
import authService from '@/services/authService'
import type { QrSession, QrSessionUser } from '@/models/qrLogin'
import type { ApiError } from '@/models/auth'
import Button from 'primevue/button'
import ProgressSpinner from 'primevue/progressspinner'
import TwoFactorVerification from './TwoFactorVerification.vue'
import QRCode from 'qrcode'

const emit = defineEmits<{
  (e: 'mode-change', payload: { mode: 'qr' | '2fa'; email?: string }): void
}>()

const authStore = useAuthStore()

const qrSession = ref<QrSession | null>(null)
const isLoading = ref(false)
const error = ref<string | null>(null)
const isExpired = ref(false)
const timeLeft = ref(0)
const qrCanvas = ref<HTMLCanvasElement | null>(null)

// 2FA state
const requires2FA = ref(false)
const twoFASessionId = ref<string | null>(null)
const twoFAUser = ref<QrSessionUser | null>(null)
const twoFAOrganizationId = ref<string | null>(null)
const maskedEmail = ref('')
const isVerifying2FA = ref(false)
const tfaComponent = ref<InstanceType<typeof TwoFactorVerification> | null>(null)

watch([requires2FA, maskedEmail], ([is2fa, email]) => {
  emit('mode-change', { mode: is2fa ? '2fa' : 'qr', email: email || undefined })
})

let pollingInterval: ReturnType<typeof setInterval> | null = null
let countdownInterval: ReturnType<typeof setInterval> | null = null

const createNewSession = async () => {
  stopPolling()
  stopCountdown()

  isLoading.value = true
  error.value = null
  isExpired.value = false
  qrSession.value = null

  try {
    const session = await qrLoginService.createSession()
    qrSession.value = session

    // Calculate time left from expires_at
    const expiresAt = new Date(session.expires_at).getTime()
    const now = Date.now()
    timeLeft.value = Math.floor((expiresAt - now) / 1000)

    // Generate QR code after next tick (when canvas is rendered)
    setTimeout(() => {
      generateQrCode(session.qr_token)
    }, 0)

    startPolling(session.session_id)
    startCountdown()
  } catch (err) {
    const apiError = err as ApiError
    error.value = apiError.error || 'Failed to create QR session'
  } finally {
    isLoading.value = false
  }
}

const generateQrCode = async (qrToken: string) => {
  if (!qrCanvas.value) return

  const qrUrl = qrLoginService.generateQrCodeUrl(qrToken)
  try {
    await QRCode.toCanvas(qrCanvas.value, qrUrl, {
      width: 200,
      margin: 2,
      color: {
        dark: '#000000',
        light: '#ffffff',
      },
    })
  } catch (err) {
    console.error('Failed to generate QR code:', err)
    error.value = 'Failed to generate QR code'
  }
}

const startPolling = (sessionId: string) => {
  pollingInterval = setInterval(async () => {
    try {
      const status = await qrLoginService.getSessionStatus(sessionId)

      if (status.status === 'approved' && status.token && status.user) {
        stopPolling()
        stopCountdown()

        // Set up authentication same as regular login
        authService.setupAuth(status.token)
        authStore.setAuth(status.token, status.user)

        window.location.href = '/dashboard/info'
      } else if (status.status === 'requires_2fa' && status.session_id && status.user) {
        // 2FA required - stop polling and show 2FA form
        stopPolling()
        stopCountdown()

        twoFASessionId.value = status.session_id
        twoFAUser.value = status.user

        // Send the 2FA code
        await send2FACode()
      } else if (status.status === 'expired') {
        stopPolling()
        stopCountdown()
        isExpired.value = true
      }
    } catch (err) {
      console.error('Polling error:', err)
      // Continue polling on error, don't stop
    }
  }, 2000)
}

const send2FACode = async () => {
  if (!twoFASessionId.value) return

  try {
    const response = await qrLoginService.send2FACode(twoFASessionId.value)
    maskedEmail.value = response.email
    twoFAOrganizationId.value = response.organization_id
    requires2FA.value = true
    tfaComponent.value?.startResendCooldown()
  } catch (err) {
    const apiError = err as ApiError & { status?: number }
    if (apiError.status === 429) {
      tfaComponent.value?.setRateLimited(true)
      requires2FA.value = true
    } else {
      error.value = apiError.error || 'Failed to send verification code'
      requires2FA.value = false
    }
  }
}

const handle2FAVerify = async (code: string) => {
  if (!twoFASessionId.value) return

  isVerifying2FA.value = true
  tfaComponent.value?.setError(null)

  try {
    const response = await qrLoginService.verify2FACode(twoFASessionId.value, code)

    // Success - set up auth and navigate
    authService.setupAuth(response.token)
    authStore.setAuth(response.token, response.user)
    if (twoFAOrganizationId.value) {
      localStorage.setItem('forceSelectOrganization', twoFAOrganizationId.value)
    }
    window.location.href = '/dashboard/info'
  } catch (err) {
    const apiError = err as ApiError & { status?: number }
    tfaComponent.value?.clearCode()

    if (apiError.status === 401) {
      tfaComponent.value?.setError('Invalid verification code')
    } else if (apiError.status === 429) {
      tfaComponent.value?.setError('Too many attempts. Please wait before trying again.')
      tfaComponent.value?.setRateLimited(true)
    } else {
      tfaComponent.value?.setError(apiError.error || 'Verification failed')
    }
  } finally {
    isVerifying2FA.value = false
  }
}

const handle2FAResend = async () => {
  if (!twoFASessionId.value) return

  tfaComponent.value?.setError(null)
  tfaComponent.value?.setRateLimited(false)

  try {
    const response = await qrLoginService.send2FACode(twoFASessionId.value)
    maskedEmail.value = response.email
    twoFAOrganizationId.value = response.organization_id
    tfaComponent.value?.setResending(false)
  } catch (err) {
    const apiError = err as ApiError & { status?: number }
    tfaComponent.value?.setResending(false)

    if (apiError.status === 429) {
      tfaComponent.value?.setRateLimited(true)
      tfaComponent.value?.setError('Please wait before requesting a new code')
    } else {
      tfaComponent.value?.setError(apiError.error || 'Failed to resend code')
    }
  }
}

const cancel2FA = () => {
  requires2FA.value = false
  twoFASessionId.value = null
  twoFAUser.value = null
  twoFAOrganizationId.value = null
  maskedEmail.value = ''
  createNewSession()
}

const stopPolling = () => {
  if (pollingInterval) {
    clearInterval(pollingInterval)
    pollingInterval = null
  }
}

const startCountdown = () => {
  countdownInterval = setInterval(() => {
    if (timeLeft.value > 0) {
      timeLeft.value--
    } else {
      stopCountdown()
      stopPolling()
      isExpired.value = true
    }
  }, 1000)
}

const stopCountdown = () => {
  if (countdownInterval) {
    clearInterval(countdownInterval)
    countdownInterval = null
  }
}

// Watch for canvas being available and regenerate QR code
watch(qrCanvas, (canvas) => {
  if (canvas && qrSession.value) {
    generateQrCode(qrSession.value.qr_token)
  }
})

onMounted(() => {
  createNewSession()
})

onUnmounted(() => {
  stopPolling()
  stopCountdown()
})
</script>

<style scoped>
.qr-login-form {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.5rem 1rem;
  text-align: center;
  min-height: 280px;
  justify-content: center;
}

.status-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 1rem;
}

.status-placeholder {
  width: 228px;
  height: 228px;
  padding: 1.25rem;
  border-radius: 14px;
  background: linear-gradient(
    180deg,
    var(--p-surface-50, rgba(103, 58, 183, 0.04)),
    var(--p-surface-100, rgba(103, 58, 183, 0.08))
  );
  border: 1px dashed var(--p-content-border-color, rgba(0, 0, 0, 0.12));
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.status-placeholder--error {
  background: linear-gradient(180deg, rgba(239, 68, 68, 0.04), rgba(239, 68, 68, 0.08));
  border-color: rgba(239, 68, 68, 0.25);
}

.status-badge {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: var(--primary-color-transparent, rgba(103, 58, 183, 0.12));
  color: var(--primary-color, #673ab7);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.85rem;
}

.status-badge i {
  font-size: 1.4rem;
}

.status-badge--error {
  background: rgba(239, 68, 68, 0.12);
  color: var(--p-red-500, #ef4444);
}

.status-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--p-text-color);
  margin: 0 0 0.25rem;
}

.status-message {
  font-size: 0.825rem;
  color: var(--p-text-muted-color);
  margin: 0;
  max-width: 22ch;
  line-height: 1.4;
}

.qr-display {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.qr-code-container {
  position: relative;
  background: white;
  padding: 0.875rem;
  border-radius: 14px;
  box-shadow:
    0 1px 2px rgba(16, 24, 40, 0.06),
    0 6px 20px rgba(16, 24, 40, 0.08);
  margin-bottom: 1.25rem;
}

.qr-code-container::before {
  content: '';
  position: absolute;
  inset: -1px;
  border-radius: 15px;
  padding: 1px;
  background: linear-gradient(135deg, var(--primary-color, #673ab7), #4f8cff);
  -webkit-mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
  mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  opacity: 0.4;
  pointer-events: none;
}

.qr-code-container canvas {
  display: block;
}

.scan-steps {
  list-style: none;
  counter-reset: step;
  padding: 0;
  margin: 0 0 1.25rem;
  font-size: 0.825rem;
  color: var(--p-text-muted-color);
  width: 100%;
  max-width: 280px;
}

.scan-steps li {
  counter-increment: step;
  position: relative;
  padding: 0.3rem 0 0.3rem 1.85rem;
  line-height: 1.4;
}

.scan-steps li::before {
  content: counter(step);
  position: absolute;
  left: 0;
  top: 0.3rem;
  width: 1.35rem;
  height: 1.35rem;
  border-radius: 50%;
  background: var(--primary-color-transparent, rgba(103, 58, 183, 0.12));
  color: var(--primary-color, #673ab7);
  font-size: 0.7rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

.scan-steps:last-child {
  margin-bottom: 0;
}

.scan-steps i {
  font-size: 0.8rem;
  vertical-align: middle;
}

.scan-steps strong,
.scan-steps__icon {
  font-weight: 600;
  color: var(--p-text-color);
}
</style>
